/**
 * Detects if the user is in Warsaw using geolocation
 * @returns Promise that resolves to true if user is in Warsaw, false otherwise
 */
export async function detectWarsawLocation(): Promise<boolean> {
  return new Promise((resolve) => {
    // Check if geolocation is available
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser');
      resolve(false);
      return;
    }

    // Check if Google Maps Geocoder is available
    if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.Geocoder) {
      console.log('Google Maps Geocoder not available yet');
      resolve(false);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0, // Don't use cached position
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Use reverse geocoding to get the city name
        geocoder.geocode(
          { location: { lat, lng } },
          (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
              // Look for city in address components
              for (const result of results) {
                const addressComponents = result.address_components || [];
                for (const component of addressComponents) {
                  const types = component.types;
                  // Check for locality (city) or administrative_area_level_2 (county)
                  if (
                    types.includes('locality') ||
                    types.includes('administrative_area_level_2')
                  ) {
                    const cityName = component.long_name.toLowerCase();
                    // Check if it's Warsaw (in Polish or English)
                    if (
                      cityName === 'warszawa' ||
                      cityName === 'warsaw' ||
                      cityName.includes('warszawa') ||
                      cityName.includes('warsaw')
                    ) {
                      console.log('User detected in Warsaw');
                      resolve(true);
                      return;
                    }
                  }
                }
              }
            }
            console.log('User not detected in Warsaw');
            resolve(false);
          }
        );
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        resolve(false);
      },
      options
    );
  });
}

/**
 * Simple distance-based check to Warsaw center
 * Warsaw center coordinates: 52.2297° N, 21.0122° E
 * @param lat - Latitude
 * @param lng - Longitude
 * @param radiusKm - Radius in kilometers (default: 30km)
 * @returns true if within radius of Warsaw center
 */
export function isNearWarsaw(
  lat: number,
  lng: number,
  radiusKm: number = 30
): boolean {
  const warsawLat = 52.2297;
  const warsawLng = 21.0122;

  // Haversine formula to calculate distance
  const R = 6371; // Earth's radius in km
  const dLat = ((lat - warsawLat) * Math.PI) / 180;
  const dLng = ((lng - warsawLng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((warsawLat * Math.PI) / 180) *
      Math.cos((lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance <= radiusKm;
}

/**
 * Detects user location and checks if they're in Warsaw
 * Uses both geocoding and distance-based check as fallback
 * @returns Promise that resolves to true if user is in Warsaw
 */
export async function isUserInWarsaw(): Promise<boolean> {
  return new Promise((resolve) => {
    // Check if geolocation is available
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser');
      resolve(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // First, try distance-based check (faster and more reliable)
        if (isNearWarsaw(lat, lng)) {
          console.log('User detected near Warsaw (distance-based check)');
          resolve(true);
          return;
        }

        // If not near Warsaw by distance, try geocoding as a fallback
        if (
          typeof window.google !== 'undefined' &&
          window.google.maps &&
          window.google.maps.Geocoder
        ) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat, lng } },
            (results, status) => {
              if (status === 'OK' && results && results.length > 0) {
                for (const result of results) {
                  const addressComponents = result.address_components || [];
                  for (const component of addressComponents) {
                    const types = component.types;
                    if (
                      types.includes('locality') ||
                      types.includes('administrative_area_level_2')
                    ) {
                      const cityName = component.long_name.toLowerCase();
                      if (
                        cityName === 'warszawa' ||
                        cityName === 'warsaw' ||
                        cityName.includes('warszawa') ||
                        cityName.includes('warsaw')
                      ) {
                        console.log('User detected in Warsaw (geocoding check)');
                        resolve(true);
                        return;
                      }
                    }
                  }
                }
              }
              resolve(false);
            }
          );
        } else {
          resolve(false);
        }
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        resolve(false);
      },
      options
    );
  });
}
